export type StagingHistoryTag = {
  id: string;
  category: string;
  label: string;
  color: string;
  active: boolean;
  prompt_json?: Record<string, unknown>;
};

export type StagingHistoryPhase = {
  label: string;
  description: string;
  prompt_en?: string;
  image_index?: number;
  image_url?: string;
};

export type StagingHistoryImage = {
  id: string;
  image_b64: string;
  role: 'start' | 'end' | null;
  label?: string;
};

export type StagingHistoryEntry = {
  id: string;
  createdAt: number;
  prompt: string;
  images: StagingHistoryImage[];
  tags: StagingHistoryTag[];
  storyboard: StagingHistoryPhase[];
  duration?: number;
  aspectRatio?: '16:9' | '9:16' | '1:1';
};

type ScopeRecord = {
  scope: string;
  entries: StagingHistoryEntry[];
};

const DB_NAME = 'seu-video-staging-history';
const DB_VERSION = 1;
const STORE = 'scopes';
export const STAGING_HISTORY_MAX = 24;

function scopeKey(userId: string, orgId: string): string {
  return `${userId}:${orgId}`;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'scope' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('IndexedDB unavailable'));
  });
}

function idbGet(scope: string): Promise<ScopeRecord | undefined> {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, 'readonly');
        const req = tx.objectStore(STORE).get(scope);
        req.onsuccess = () => resolve(req.result as ScopeRecord | undefined);
        req.onerror = () => reject(req.error);
        tx.oncomplete = () => db.close();
      }),
  );
}

function idbPut(record: ScopeRecord): Promise<void> {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).put(record);
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = () => reject(tx.error);
      }),
  );
}

export function fingerprintStagingSnapshot(
  entry: Pick<StagingHistoryEntry, 'prompt' | 'images' | 'tags' | 'storyboard'>,
): string {
  const imgSig = entry.images
    .map((i) => `${i.role}:${i.image_b64.length}:${i.image_b64.slice(24, 56)}`)
    .join('|');
  const tagSig = entry.tags
    .filter((t) => t.active)
    .map((t) => t.id)
    .sort()
    .join(',');
  const sbSig = entry.storyboard
    .map((p) => `${p.label}|${p.description}|${p.image_index ?? ''}`)
    .join(';;');
  return `${entry.prompt.trim()}\n${imgSig}\n${tagSig}\n${sbSig}`;
}

function newEntryId(): string {
  return `sh-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export async function listStagingHistory(userId: string, orgId: string): Promise<StagingHistoryEntry[]> {
  try {
    const rec = await idbGet(scopeKey(userId, orgId));
    return rec?.entries || [];
  } catch (err) {
    console.warn('staging history list', err);
    return [];
  }
}

export type StagingHistoryDraft = Omit<StagingHistoryEntry, 'id' | 'createdAt'>;

export async function appendStagingHistory(
  userId: string,
  orgId: string,
  draft: StagingHistoryDraft,
): Promise<{ entry: StagingHistoryEntry; created: boolean } | null> {
  if (!draft.prompt.trim() || draft.storyboard.length === 0) return null;

  const scope = scopeKey(userId, orgId);
  const fp = fingerprintStagingSnapshot(draft);

  const persist = async (entries: StagingHistoryEntry[]): Promise<{ entry: StagingHistoryEntry; created: boolean }> => {
    const latest = entries[0];
    if (latest && fingerprintStagingSnapshot(latest) === fp) {
      return { entry: latest, created: false };
    }
    const entry: StagingHistoryEntry = {
      ...draft,
      id: newEntryId(),
      createdAt: Date.now(),
    };
    const next = [entry, ...entries].slice(0, STAGING_HISTORY_MAX);
    await idbPut({ scope, entries: next });
    return { entry, created: true };
  };

  try {
    const rec = await idbGet(scope);
    return await persist(rec?.entries || []);
  } catch (err) {
    const quota = err instanceof DOMException && err.name === 'QuotaExceededError';
    if (!quota) {
      console.warn('staging history save', err);
      return null;
    }
    try {
      const rec = await idbGet(scope);
      const trimmed = (rec?.entries || []).slice(0, Math.max(4, Math.floor(STAGING_HISTORY_MAX / 2)));
      return await persist(trimmed);
    } catch (retryErr) {
      console.warn('staging history quota', retryErr);
      return null;
    }
  }
}
